/**
 * Bridge global para sistema de notificações
 * 
 * Permite que código não-React (como api.ts, services) use o
 * sistema de notificações centralizado através de uma função global.
 * 
 * Esta função deve ser inicializada pelo NotificationProvider.
 */

import type { NotificationItem } from '../components/feedback/NotificationProvider';
import { logger } from './terminalLogger';

/**
 * Parâmetros para notificação (sem id e createdAt que são gerados automaticamente)
 */
export type NotificationParams = Omit<NotificationItem, 'id' | 'createdAt'>;

/**
 * Função global de notificação
 * Inicializada pelo NotificationProvider no mount
 */
let globalNotify: ((params: NotificationParams) => string) | null = null;

/**
 * Função global de dismiss
 * Inicializada pelo NotificationProvider no mount
 */
let globalDismiss: ((id?: string) => void) | null = null;

/**
 * Define a função global de notificação
 * Deve ser chamada pelo NotificationProvider
 */
export function setGlobalNotify(notifyFn: (params: NotificationParams) => string) {
  globalNotify = notifyFn;
}

/**
 * Define a função global de dismiss
 * Deve ser chamada pelo NotificationProvider
 */
export function setGlobalDismiss(dismissFn: (id?: string) => void) {
  globalDismiss = dismissFn;
}

/**
 * Limpa a função global de notificação
 * Deve ser chamada pelo NotificationProvider no unmount
 */
export function clearGlobalNotify() {
  globalNotify = null;
  globalDismiss = null;
}

/**
 * Emite notificação usando o sistema centralizado
 * Retorna o ID da notificação ou string vazia se não foi possível notificar
 * 
 * @param params Parâmetros da notificação
 * @returns ID da notificação ou string vazia
 */
export function notify(params: NotificationParams): string {
  if (globalNotify) {
    try {
      return globalNotify(params);
    } catch (error) {
      logger.error('[GLOBAL_NOTIFY] Erro ao usar sistema centralizado:', error);
      // Não há fallback para notificações - apenas logar o erro
      return '';
    }
  }

  // Se sistema não estiver disponível, apenas logar (não quebrar o fluxo)
  logger.warn('[GLOBAL_NOTIFY] Sistema de notificações não disponível', {
    type: params.type,
    title: params.title,
    message: params.message
  });
  
  return '';
}

/**
 * Remove uma notificação pelo ID
 * 
 * @param id ID da notificação a ser removida
 */
export function dismissNotification(id?: string): void {
  if (globalDismiss && id) {
    try {
      globalDismiss(id);
    } catch (error) {
      logger.error('[GLOBAL_NOTIFY] Erro ao remover notificação:', error);
    }
  }
}

